import * as StyleVariables from '@packages/systems/core/utils/StyleVariables';
import {
  makeCodeModuleId,
  type CodeModuleId,
} from '@packages/systems/code-modules/core';
import type {Plugins} from '@packages/systems/wfdl';

const CUSTOM_PROPERTY_NAME = /^--[A-Za-z0-9_-]+$/;

/**
 * Builds one `:host` sheet per code module that maps the names the module
 * authored to the site's CSS variables.
 */
export function buildCodeLibraryAliasSheets({
  variables,
  collections,
  slugMap,
}: {
  variables: StyleVariables.VariableMap;
  collections: StyleVariables.VariableCollectionMap;
  slugMap: Plugins.PluginsType['slugMap'];
}): Map<CodeModuleId, string> {
  const winners = new Map<
    CodeModuleId,
    Map<string, {variableId: string; siteName: string}>
  >();

  for (const variable of variables) {
    if (variable.deleted || variable.alias?.target.type !== 'CodeModule') {
      continue;
    }
    const {error, result} = StyleVariables.safeRenderCssVariableName(
      variables,
      collections,
      slugMap,
      variable.id
    );
    if (error) continue;

    const moduleId = makeCodeModuleId(variable.alias.target.id);
    const declarations = winners.get(moduleId) ?? new Map();
    for (const name of variable.alias.names) {
      // The sheet is injected into style tags, so only plain names get through
      if (!CUSTOM_PROPERTY_NAME.test(name)) continue;
      const current = declarations.get(name);
      // Lowest id wins, so the pick does not depend on iteration order
      if (!current || variable.id < current.variableId) {
        declarations.set(name, {variableId: variable.id, siteName: result});
      }
    }
    if (declarations.size > 0) winners.set(moduleId, declarations);
  }

  const sheets = new Map<CodeModuleId, string>();
  for (const [moduleId, declarations] of winners) {
    const body = [...declarations]
      // Same name: the page value already inherits, and `--x:var(--x)` is a cycle
      .filter(([name, {siteName}]) => name !== `--${siteName}`)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([name, {siteName}]) => `${name}:var(--${siteName});`)
      .join('');
    if (body) sheets.set(moduleId, `:host{${body}}`);
  }
  return sheets;
}
